export interface ParsedFilePreview {
  readonly headers: string[];
  readonly rows: Record<string, unknown>[];
  readonly error?: string;
}

function parseJsonContent(
  content: { headers?: string[] } & Record<string, unknown>,
): { headers: string[]; rows: Record<string, unknown>[] } {
  const jsonData = content?.['data'] ?? content;
  if (Array.isArray(jsonData) && jsonData.length > 0) {
    const first = jsonData[0] as Record<string, unknown>;
    return {
      headers: Object.keys(first),
      rows: jsonData as Record<string, unknown>[],
    };
  }
  if (typeof jsonData === 'object' && jsonData !== null) {
    const arr = Array.isArray(jsonData) ? jsonData : [jsonData];
    return {
      headers:
        arr.length > 0 ? Object.keys(arr[0] as Record<string, unknown>) : [],
      rows: arr as Record<string, unknown>[],
    };
  }
  return { headers: [], rows: [] };
}

function parseUnknownFormat(
  content: { headers?: string[]; data?: unknown[] } & Record<string, unknown>,
): { headers: string[]; rows: Record<string, unknown>[]; error?: string } {
  if (content?.['headers'] && Array.isArray(content?.['data'])) {
    return {
      headers: content['headers'] as string[],
      rows: content['data'] as Record<string, unknown>[],
    };
  }
  if (
    Array.isArray(content) &&
    content.length > 0 &&
    typeof content[0] === 'object'
  ) {
    return {
      headers: Object.keys(content[0] as object),
      rows: content as Record<string, unknown>[],
    };
  }
  return {
    headers: [],
    rows: [],
    error: 'Unsupported file format for preview',
  };
}

/** Normalises generatePreview API payload into headers + tabular rows. */
export function parseFilePreviewPayload(previewData: unknown): ParsedFilePreview {
  if (!previewData) {
    return { headers: [], rows: [], error: 'No preview data received' };
  }

  let actualData = previewData as Record<string, unknown>;
  const pd = previewData as Record<string, unknown>;
  if (pd?.['data'] && typeof pd['data'] === 'object') {
    const dataContent = (pd['data'] as Record<string, unknown>)['content'];
    if (dataContent !== undefined) {
      actualData = {
        content: dataContent,
        format:
          (pd['data'] as Record<string, unknown>)['format'] ?? pd['format'],
      };
    }
  } else if (Array.isArray((pd?.['result'] as unknown[])?.[0])) {
    actualData = (pd['result'] as unknown[])[0] as Record<string, unknown>;
  } else if (pd?.['success'] && pd?.['data']) {
    actualData = pd['data'] as Record<string, unknown>;
  }

  const content = (actualData?.['content'] ||
    (actualData?.['headers'] ? actualData : null)) as
    | {
        headers?: string[];
        data?: Record<string, unknown>[];
        previewSupported?: boolean;
        message?: string;
      }
    | null;
  const format = String(actualData?.['format'] || '').toLowerCase();

  if (!content) {
    return {
      headers: [],
      rows: [],
      error:
        'Invalid preview response structure. Expected format with headers and data.',
    };
  }

  if (content.previewSupported === false) {
    return {
      headers: [],
      rows: [],
      error:
        typeof content.message === 'string' ? content.message : 'No preview found',
    };
  }

  let headers: string[] = [];
  let rows: Record<string, unknown>[] = [];

  switch (format) {
    case 'csv':
    case 'tsv':
    case 'xlsx':
    case 'parquet':
      headers = (content?.['headers'] as string[] | undefined) || [];
      rows =
        (content?.['data'] as Record<string, unknown>[] | undefined) || [];
      break;
    case 'json':
    case 'xml': {
      const p = parseJsonContent(content);
      headers = p.headers;
      rows = p.rows;
      break;
    }
    default: {
      const p = parseUnknownFormat(content);
      headers = p.headers;
      rows = p.rows;
      if (p.error) {
        return { headers: [], rows: [], error: p.error };
      }
    }
  }

  return { headers, rows };
}
